<app-upgrade-prompt [message]="upgradeMessage"></app-upgrade-prompt>

<div class="iw">

  <!-- ── Page Header ────────────────────────────── -->
  <div class="iw__header">
    <div>
      <h1 class="iw__title">Invite Team Member</h1>
      <p class="iw__subtitle">
        Add a field worker or an admin/supervisor to your company.
      </p>
    </div>
    <a routerLink="/workers" class="iw__back-btn">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
        <path d="M19 12H5M5 12L12 19M5 12L12 5"
              stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      Back to Workers
    </a>
  </div>

  <!-- ── Main Grid ───────────────────────────────── -->
  <div class="iw__grid">

    <!-- ── Form Card ─────────────────────────────── -->
    <div class="iw__form-card">

      <div class="iw__card-head">
        <div class="iw__card-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M16 21V19C16 16.8 14.2 15 12 15H5C2.8 15 1 16.8 1 19V21"
                  stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
            <circle cx="8.5" cy="7" r="4" stroke="currentColor" stroke-width="1.8"/>
            <line x1="20" y1="8" x2="20" y2="14" stroke="currentColor"
                  stroke-width="1.8" stroke-linecap="round"/>
            <line x1="17" y1="11" x2="23" y2="11" stroke="currentColor"
                  stroke-width="1.8" stroke-linecap="round"/>
          </svg>
        </div>
        <div>
          <h2 class="iw__card-title">Team Member Details</h2>
          <p class="iw__card-desc">
            A temporary password will be auto-generated and returned.
          </p>
        </div>
      </div>

      <form (ngSubmit)="invite()" #inviteForm="ngForm" novalidate>

        <!-- ── Role Selector ───────────────────── -->
        <div class="iw__field">
          <label class="iw__label">
            Role
            <span class="iw__required">*</span>
          </label>
          <div class="iw__role-grid">
            <div
              *ngFor="let r of roles"
              class="iw__role-card"
              [class.iw__role-card--selected]="model.role === r.value"
              (click)="selectRole(r.value)">
              <div class="iw__role-top">
                <span class="iw__role-emoji">{{ r.icon }}</span>
                <div class="iw__role-check"
                     [class.iw__role-check--active]="model.role === r.value">
                  <svg *ngIf="model.role === r.value"
                       width="10" height="10" viewBox="0 0 24 24" fill="none">
                    <path d="M5 13l4 4L19 7" stroke="white" stroke-width="3"
                          stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
              </div>
              <div class="iw__role-label">{{ r.label }}</div>
              <div class="iw__role-desc">{{ r.description }}</div>
            </div>
          </div>

          <!-- ✅ Admin context note -->
          <div class="iw__role-note" *ngIf="model.role === 'Admin'">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.8"/>
              <path d="M12 8v4M12 16h.01" stroke="currentColor" stroke-width="2"
                    stroke-linecap="round"/>
            </svg>
            Admins log into the web dashboard, not the mobile app. They can create and
            approve permits created by other admins — they cannot
            approve their own permits.
          </div>

          <div class="iw__role-note iw__role-note--worker" *ngIf="model.role === 'Worker'">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.8"/>
              <path d="M12 8v4M12 16h.01" stroke="currentColor" stroke-width="2"
                    stroke-linecap="round"/>
            </svg>
            Workers log into the KinexOps Mobile app only.
            They will receive their credentials and should download the app to get started.
          </div>
        </div>

        <!-- ── Full Name ────────────────────────── -->
        <div class="iw__field">
          <label class="iw__label" for="fullName">
            Full Name
            <span class="iw__required">*</span>
          </label>
          <div class="iw__input-wrap"
               [class.iw__input-wrap--error]="nameField.invalid && nameField.touched">
            <span class="iw__input-icon">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="8" r="4" stroke="currentColor" stroke-width="1.8"/>
                <path d="M4 20C4 17 7.6 14 12 14C16.4 14 20 17 20 20"
                      stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
              </svg>
            </span>
            <input
              id="fullName"
              class="iw__input"
              type="text"
              [placeholder]="model.role === 'Admin' ? 'e.g. Sarah Adeyemi' : 'e.g. Emeka Okafor'"
              [(ngModel)]="model.fullName"
              name="fullName"
              #nameField="ngModel"
              required
              minlength="2"
              autocomplete="off"/>
          </div>
          <span class="iw__field-error"
                *ngIf="nameField.invalid && nameField.touched">
            Please enter the full name.
          </span>
        </div>

        <!-- ── Email ────────────────────────────── -->
        <div class="iw__field">
          <label class="iw__label" for="email">
            Email Address
            <span class="iw__required">*</span>
          </label>
          <div class="iw__input-wrap"
               [class.iw__input-wrap--error]="emailField.invalid && emailField.touched">
            <span class="iw__input-icon">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z"
                      stroke="currentColor" stroke-width="1.8"/>
                <polyline points="22,6 12,13 2,6"
                          stroke="currentColor" stroke-width="1.8"
                          stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
            <input
              id="email"
              class="iw__input"
              type="email"
              [placeholder]="model.role === 'Admin'
                ? 'e.g. sarah@company.com'
                : 'e.g. emeka@company.com'"
              [(ngModel)]="model.email"
              name="email"
              #emailField="ngModel"
              required
              email
              autocomplete="off"/>
          </div>
          <span class="iw__field-error"
                *ngIf="emailField.invalid && emailField.touched">
            Please enter a valid email address.
          </span>
        </div>

        <!-- ── Error Banner ──────────────────────── -->
        <div class="iw__error-banner" *ngIf="error">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.8"/>
            <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor"
                  stroke-width="1.8" stroke-linecap="round"/>
            <circle cx="12" cy="16" r="0.5" fill="currentColor"
                    stroke="currentColor" stroke-width="1"/>
          </svg>
          <span>{{ error }}</span>
        </div>

        <!-- ── Submit ────────────────────────────── -->
        <button
          class="iw__submit-btn"
          type="submit"
          [disabled]="loading || inviteForm.invalid">
          <span class="iw__btn-spinner" *ngIf="loading"></span>
          <svg *ngIf="!loading" width="15" height="15" viewBox="0 0 24 24" fill="none">
            <path d="M16 21V19C16 16.8 14.2 15 12 15H5C2.8 15 1 16.8 1 19V21"
                  stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
            <circle cx="8.5" cy="7" r="4" stroke="currentColor" stroke-width="1.8"/>
            <line x1="20" y1="8" x2="20" y2="14" stroke="currentColor"
                  stroke-width="1.8" stroke-linecap="round"/>
            <line x1="17" y1="11" x2="23" y2="11" stroke="currentColor"
                  stroke-width="1.8" stroke-linecap="round"/>
          </svg>
          {{ loading
            ? 'Sending Invite…'
            : (model.role === 'Admin' ? 'Send Admin Invite' : 'Send Worker Invite') }}
        </button>

      </form>
    </div>

    <!-- ── Right Column ──────────────────────────── -->
    <div class="iw__right">

      <!-- Success Card -->
      <div class="iw__success-card" *ngIf="success">
        <div class="iw__success-head">
          <div class="iw__success-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2.5"
                    stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div>
            <h3 class="iw__success-title">Invite Sent!</h3>
            <p class="iw__success-sub">Share these credentials with the team member.</p>
          </div>
        </div>

        <div class="iw__success-body">
          <div class="iw__cred-row">
            <span class="iw__cred-label">Full Name</span>
            <span class="iw__cred-value">{{ success.fullName }}</span>
          </div>
          <div class="iw__cred-row">
            <span class="iw__cred-label">Email</span>
            <span class="iw__cred-value iw__cred-value--email">{{ success.email }}</span>
          </div>
          <div class="iw__cred-row iw__cred-row--password">
            <span class="iw__cred-label">Temp Password</span>
            <div class="iw__password-wrap">
              <code class="iw__cred-password">{{ success.temporaryPassword }}</code>
              <button class="iw__copy-btn"
                      (click)="copyPassword()"
                      [class.iw__copy-btn--copied]="copied"
                      title="Copy password">
                <svg *ngIf="!copied" width="13" height="13" viewBox="0 0 24 24" fill="none">
                  <rect x="9" y="9" width="13" height="13" rx="2"
                        stroke="currentColor" stroke-width="1.8"/>
                  <path d="M5 15H4C2.9 15 2 14.1 2 13V4C2 2.9 2.9 2 4 2H13C14.1 2 15 2.9 15 4V5"
                        stroke="currentColor" stroke-width="1.8"/>
                </svg>
                <svg *ngIf="copied" width="13" height="13" viewBox="0 0 24 24" fill="none">
                  <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2.2"
                        stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                {{ copied ? 'Copied!' : 'Copy' }}
              </button>
            </div>
          </div>
        </div>

        <!-- ✅ Role-specific instruction after invite -->
        <div class="iw__success-instruction">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                  stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
            <line x1="12" y1="9" x2="12" y2="13" stroke="currentColor"
                  stroke-width="1.6" stroke-linecap="round"/>
            <circle cx="12" cy="17" r="0.5" fill="currentColor"
                    stroke="currentColor" stroke-width="1"/>
          </svg>
          <span *ngIf="success.role === 'Admin'">
            This person should log into the
            <strong>KinexOps web dashboard</strong> using these credentials.
            They can create and approve permits, manage jobs, and view reports.
          </span>
          <span *ngIf="success.role !== 'Admin'">
            This person should log into the
            <strong>KinexOps Mobile app</strong> using these credentials.
            They should change their password on first login.
          </span>
        </div>

        <button class="iw__invite-another" (click)="resetSuccess()">
          Invite Another Team Member
        </button>
      </div>

      <!-- Info Card (shown before success) -->
      <div class="iw__info-card" *ngIf="!success">
        <h3 class="iw__info-title">Who can do what</h3>
        <div class="iw__role-compare">

          <div class="iw__compare-col">
            <div class="iw__compare-header iw__compare-header--admin">
              🏢 Admin
            </div>
            <ul class="iw__compare-list">
              <li>Web dashboard access</li>
              <li>Create permits</li>
              <li>Approve permits from others</li>
              <li>Manage jobs &amp; workers</li>
              <li>View live map</li>
              <li>Generate reports</li>
            </ul>
          </div>

          <div class="iw__compare-col">
            <div class="iw__compare-header iw__compare-header--worker">
              👷 Worker
            </div>
            <ul class="iw__compare-list">
              <li>Mobile app access</li>
              <li>View assigned jobs</li>
              <li>Submit work logs</li>
              <li>Accept permits</li>
              <li>Update checklists</li>
              <li>GPS tracked on map</li>
            </ul>
          </div>

        </div>
      </div>

    </div>

  </div>
</div>